// ── PARENT: BILLING + PAYMENT HISTORY ──────────────────────────────────
// esc (shared classic script) stays a bare global — not migrated yet.
// cardModal/payNow/downloadReceipt/phSetSeason/phSetStatus/exportMyHistory
// wired via VIEW_INIT (phase 2 of the onclick → addEventListener migration)
// instead of window.
import { DB, session, currentView } from '../core/state.js';
import { TODAY, fmtD } from '../core/format.js';
import { childName } from '../core/domain-utils.js';
import { VIEWS, VIEW_INIT } from '../core/routing.js';
import { exportCSV } from '../core/ui.js';
import { CARD_LOGOS, renderChargeHistory, phSeasonRange, cardModal, payNow, downloadReceipt } from './billing-shared.js';

// ── PARENT: BILLING ────────────────────────────────────────────────
VIEWS['p-billing'] = () => {
  const charges = DB.charges.filter(c => c.accountId === session.id).slice().sort((a,b) => b.date.localeCompare(a.date));
  const due = charges.filter(c => c.status === 'due');
  const dueTotal = due.reduce((s,c) => s + c.amount, 0);

  return `
    <div class="page-head">
      <h2 class="serif">Billing</h2>
    </div>
    <div class="grid-2">
      <div style="display:flex;flex-direction:column;gap:18px">
        ${due.length ? `<div class="card" style="border-color:rgba(248,113,113,0.35)">
          <div class="card-hd" style="color:var(--red)">Balance due — ${fmt$(dueTotal)}</div>
          ${due.map(c => `<div style="display:flex;justify-content:space-between;font-size:12px;color:var(--muted);padding:4px 0">${esc(c.desc)}<span>${fmt$(c.amount)}</span></div>`).join('')}
          <button class="btn" id="payNowBtn" data-action="pay-now" style="margin-top:14px">Pay now</button>
          <p style="font-size:11px;color:var(--muted);text-align:center;margin-top:10px">Secure checkout via Stripe.</p>
        </div>` : ''}
        <div class="card">
          <div class="card-hd">Payment method</div>
          ${session.card ? (() => {
            const [expM, expY] = session.card.exp.split('/').map(Number);
            const expYear = 2000 + expY;
            const now = TODAY;
            const expired  = expYear < now.getFullYear() || (expYear === now.getFullYear() && expM < now.getMonth() + 1);
            const expiring = !expired && expYear === now.getFullYear() && expM === now.getMonth() + 1;
            const flag = expired
              ? `<div style="margin-top:10px;padding:8px 12px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.3);border-radius:7px;font-size:12px;color:var(--red)">This card has expired. Please update your payment method to avoid missed charges.</div>`
              : expiring
              ? `<div style="margin-top:10px;padding:8px 12px;background:rgba(251,191,36,0.07);border:1px solid rgba(251,191,36,0.3);border-radius:7px;font-size:12px;color:#fbbf24">This card expires this month. Consider updating it before your next billing date.</div>`
              : '';
            return `
            <div style="display:flex;align-items:center;gap:14px">
              <div style="display:flex;align-items:center;justify-content:center;min-width:44px;flex-shrink:0">${CARD_LOGOS[session.card.brand] || `<span style="font-size:11px;font-weight:700;color:#333">${esc(session.card.brand)}</span>`}</div>
              <div style="flex:1">
                <div style="font-size:14px">${esc(session.card.brand)} ending in ${esc(session.card.last4)}</div>
                <div style="font-size:12px;color:${expired ? 'var(--red)' : expiring ? '#fbbf24' : 'var(--muted)'}">Expires ${esc(session.card.exp)} · stored securely with Stripe</div>
              </div>
              <button class="btn-ghost btn-sm" data-action="card-modal">Update</button>
            </div>
            ${flag}`;
          })() : `
            <p class="empty" style="padding-top:0">No card on file — add one so tuition can run automatically.</p>
            <button class="btn-ghost" data-action="card-modal">Add a card</button>`}
        </div>
      </div>
      <div class="card">
        <div class="card-hd">Recent charges</div>
        ${(() => {
          const byDate = {};
          charges.slice(0, 5).forEach(c => { (byDate[c.date] = byDate[c.date] || []).push(c); });
          const invoices = Object.entries(byDate).sort((a,b) => b[0].localeCompare(a[0]));
          if (!invoices.length) return '<p class="empty">No charges yet.</p>';
          return invoices.map(([date, rows]) => {
            const total   = rows.reduce((s,c) => s + c.amount, 0);
            const allSame = rows.every(r => r.status === rows[0].status);
            const status  = allSame ? rows[0].status : 'mixed';
            const pillCls = status === 'paid' ? 'pill-paid' : status === 'due' ? 'pill-due' : status === 'refunded' ? 'pill-refunded' : 'pill-off';
            const pillLbl = status === 'mixed' ? 'partial' : status;
            return `
            <div style="padding:10px 0;border-bottom:1px solid var(--border-soft)">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
                <div style="display:flex;align-items:center;gap:10px">
                  <span style="font-size:13px;color:var(--cream);font-weight:500">${fmtD(date)}</span>
                  <span class="pill ${pillCls}" style="font-size:10px">${pillLbl}</span>
                </div>
                <div style="display:flex;align-items:center;gap:12px">
                  <span class="amt" style="font-size:15px${rows.some(c => c.status === 'due') ? ';color:var(--red)' : ''}">${fmt$(total)}</span>
                  ${status !== 'due' ? `<button data-action="download-receipt" data-date="${date}" style="font-size:11px;color:var(--gold);background:none;border:none;cursor:pointer;padding:0;white-space:nowrap;font-family:inherit;flex-shrink:0">Receipt &darr;</button>` : ''}
                </div>
              </div>
              ${rows.map(c => `
                <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;padding:3px 0 3px 10px;border-left:2px solid var(--border-soft)">
                  <div style="font-size:12px;color:var(--muted);min-width:0">
                    ${c.childId ? `<span style="color:var(--cream)">${esc(childName(c.childId))}</span> · ` : ''}${esc(c.desc)}
                  </div>
                  <span style="font-size:12px;color:var(--gold-light);flex-shrink:0">${fmt$(c.amount)}</span>
                </div>`).join('')}
            </div>`;
          }).join('') + `
          <button onclick="route('p-history')" style="margin-top:12px;width:100%;padding:8px;background:transparent;border:1px dashed rgba(201,168,76,0.3);border-radius:7px;color:var(--gold);font-size:13px;font-family:inherit;cursor:pointer">
            Show more &rsaquo;
          </button>`;
        })()}
      </div>
    </div>`;
};

VIEW_INIT['p-billing'] = () => {
  document.querySelectorAll('[data-action="pay-now"]').forEach(btn => {
    btn.addEventListener('click', payNow);
  });
  document.querySelectorAll('[data-action="card-modal"]').forEach(btn => {
    btn.addEventListener('click', () => cardModal());
  });
  document.querySelectorAll('[data-action="download-receipt"]').forEach(btn => {
    btn.addEventListener('click', () => downloadReceipt(session.id, btn.dataset.date));
  });
};

// ── PARENT: PAYMENT HISTORY ───────────────────────────────────────────
let phSeason = 'current';
let phStatus = '';

function phSetSeason(v) { phSeason = v; renderHistoryTable(); }
function phSetStatus(v) { phStatus = v; renderHistoryTable(); }

function renderHistoryTable() {
  renderChargeHistory({ accountId: session.id, season: phSeason, status: phStatus, targetId: 'phTableWrap' });
}

VIEWS['p-history'] = () => {
  phSeason = 'all'; phStatus = '';
  const seasonStartYear = (TODAY.getMonth() + 1) >= 9 ? TODAY.getFullYear() : TODAY.getFullYear() - 1;
  const html = `
    <div class="page-head">
      <span class="eyebrow check-row" onclick="route('p-billing')">‹ Billing</span>
      <h2 class="serif">Payment <em>History</em></h2>
    </div>
    <div class="card">
      <div class="card-hd">
        All charges
        <button class="btn-ghost btn-sm" data-action="export-history">Download CSV</button>
      </div>
      <div class="filters" style="margin-bottom:16px">
        <div class="field" style="margin-bottom:0">
          <label>Season</label>
          <select id="phSeasonSel">
            <option value="all" selected>All time</option>
            <option value="current">${seasonStartYear}–${seasonStartYear+1} season</option>
            <option value="last">${seasonStartYear-1}–${seasonStartYear} season</option>
          </select>
        </div>
        <div class="field" style="margin-bottom:0">
          <label>Status</label>
          <select id="phStatusSel">
            <option value="">All</option>
            <option value="paid">Paid</option>
            <option value="due">Due</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>
      <div id="phTableWrap"></div>
    </div>`;
  setTimeout(renderHistoryTable, 0);
  return html;
};

VIEW_INIT['p-history'] = () => {
  document.querySelectorAll('[data-action="export-history"]').forEach(btn => {
    btn.addEventListener('click', exportMyHistory);
  });
  const seasonSel = document.getElementById('phSeasonSel');
  if (seasonSel) seasonSel.addEventListener('change', () => phSetSeason(seasonSel.value));
  const statusSel = document.getElementById('phStatusSel');
  if (statusSel) statusSel.addEventListener('change', () => phSetStatus(statusSel.value));
};

function exportMyHistory() {
  const onHistoryPage = currentView === 'p-history';
  const { from, to } = onHistoryPage ? phSeasonRange(phSeason) : { from: '2000-01-01', to: '2099-12-31' };
  const rows = DB.charges.filter(c =>
    c.accountId === session.id &&
    c.date >= from && c.date <= to &&
    (!onHistoryPage || !phStatus || c.status === phStatus)
  ).sort((a,b) => b.date.localeCompare(a.date))
    .map(c => [c.date, childName(c.childId), c.desc, c.type, c.amount, c.status]);
  exportCSV('payment-history.csv', ['Date','Child','Description','Type','Amount','Status'], rows);
}
